import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useAuth } from "../store/auth";
import { formatUsd } from "../lib/format";
import { MarketCard, type Market } from "../components/MarketCard";

export function AccountPage() {
  const { user, logout } = useAuth();
  const w = useQuery({
    queryKey: ["wallet"],
    queryFn: () => api<{ availableMicro: string }>("/wallet"),
    enabled: !!user,
  });
  if (!user) {
    return (
      <div className="wrap empty">
        <h3>Account</h3>
        <Link to="/login" className="btn btn-primary">Sign in</Link>
      </div>
    );
  }
  return (
    <div className="wrap">
      <div className="page-head">
        <span className="kicker">Account</span>
        <h1>{user.username}</h1>
        <p>{user.email} · KYC {user.kyc_status}</p>
      </div>
      <p>Available {formatUsd(w.data?.availableMicro ?? 0)}</p>
      <p>Referral code <strong>{user.referral_code}</strong></p>
      <div className="chips">
        <Link className="chip" to="/wallet">Wallet</Link>
        <Link className="chip" to="/portfolio">Portfolio</Link>
        <Link className="chip" to="/help">Support</Link>
        <Link className="chip" to="/responsible-trading">Limits</Link>
      </div>
      <button className="btn btn-ghost" style={{ marginTop: 16 }} onClick={() => void logout()}>Sign out</button>
    </div>
  );
}

export function ActivityPage() {
  const { user } = useAuth();
  const q = useQuery({
    queryKey: ["activity"],
    queryFn: () => api<{ trades: Array<Record<string, string>>; notifications: Array<Record<string, string>> }>("/portfolio/activity"),
    enabled: !!user,
  });
  if (!user) {
    return (
      <div className="wrap empty">
        <h3>Sign in to see activity</h3>
        <p>Fills and notices land here after you trade.</p>
        <Link to="/login" className="btn btn-primary">Sign in</Link>
      </div>
    );
  }
  return (
    <div className="wrap">
      <h1>Activity</h1>
      <h3>Notifications</h3>
      {(q.data?.notifications ?? []).length === 0 && (
        <div className="card empty" style={{ padding: 24 }}>
          <h3>You’re all caught up</h3>
          <p>Fills, resolutions and account notes will show here.</p>
        </div>
      )}
      {(q.data?.notifications ?? []).map((n) => (
        <div key={n.id} className="card" style={{ marginBottom: 8 }}><strong>{n.title}</strong><p>{n.body}</p></div>
      ))}
      <h3>Trades</h3>
      {(q.data?.trades ?? []).length === 0 && (
        <p style={{ color: "var(--muted)" }}>Your fills will list here.</p>
      )}
      <table className="table">
        <thead><tr><th>Market</th><th>Price</th><th>Size</th></tr></thead>
        <tbody>
          {(q.data?.trades ?? []).map((t) => (
            <tr key={t.id}><td>{t.question}</td><td>{t.price_cents}¢</td><td>{t.quantity_shares}</td></tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function WatchlistPage() {
  const { user } = useAuth();
  const q = useQuery({
    queryKey: ["wl"],
    queryFn: () => api<{ markets: Market[] }>("/portfolio/watchlist"),
    enabled: !!user,
  });
  if (!user) {
    return (
      <div className="wrap empty">
        <h3>Sign in to save markets</h3>
        <Link to="/login" className="btn btn-primary">Sign in</Link>
      </div>
    );
  }
  if (q.isLoading) return <div className="wrap"><div className="skeleton" style={{ height: 28 }} /></div>;
  if (!q.data?.markets.length) {
    return (
      <div className="wrap empty">
        <h3>Nothing on your watchlist yet</h3>
        <p>Star a market from any contract page to keep it here.</p>
        <Link to="/markets" className="btn btn-primary">Browse markets</Link>
      </div>
    );
  }
  return (
    <div className="wrap">
      <h1>Watchlist</h1>
      <div className="grid-3">
        {q.data.markets.map((m) => <MarketCard key={m.id || m.slug} market={m} />)}
      </div>
    </div>
  );
}
